// K8 Phase 1 — the "machine in use" card, shared by the court's Ball Machine
// page (audience 'court': takings + the court's side, never the owner's
// figure) and the super admin pages (audience 'owner': all three figures).
// Everything on it is EXPECTED: the phone's final row decides, the customer
// may extend or stop early, and the copy says so every time.
//
// K8 P2.5 P1 — renders the PAUSED state: a phone that stops talking mid-rental
// has its figures frozen at last contact by the server (`stale`), and the card
// says so rather than running a countdown nobody is feeding.
//
// K8 P2.5 P2 — the front-desk treatment (same as /sports/live): status colour
// by urgency, a live countdown, a thin progress bar, gradient depth and glow.
// Five honest states: in progress, wrapping up, ending soon, past its end,
// paused; open time has no end and shows what has been played so far. The
// colours, countdown and pulse come from ../liveUi so both boards read as one.
import { useEffect, useState } from 'react';
import { Activity, WifiOff } from 'lucide-react';
import { GREEN, GREY, AMBER, fmtCentavos, fmtAgo } from './KioskDialogs';
import { urgency, fmtCountdown, fmtHrMin, LIVE_OVERRUN, LIVE_OPEN, LIVE_PAUSED, ensureLiveKeyframes } from '../liveUi';

const hhmm = (ms) => (ms ? new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—');

export const STOP_LABEL = {
  WEATHER: 'Rain or weather', MACHINE_FAULT: 'Machine problem', KIOSK_FAULT: 'Kiosk problem',
  COURT_CLOSED: 'Court closed early', EMERGENCY: 'Emergency', VIOLATION: 'Terms violation', ABANDONED: 'Abandoned',
  GOODWILL: 'Goodwill', MISTAKE: 'Started by mistake', OTHER: 'Owner override',
};

/* ── State derivation ───────────────────────────────────────────── */

// Everything the card needs to draw, from the server's live block and a clock.
// Pure, so it is testable and the same maths feeds the one-liner and the card.
// A paused card is evaluated at the server's `as_at_ms` (last contact), never
// at `now`, so its numbers do not move.
export function liveState(live, nowMs = Date.now()) {
  if (!live || live.started_at_ms == null) return null;
  const stale = !!live.stale;
  const fixed = live.expected_end_ms != null;
  const asAt = stale ? (live.as_at_ms ?? live.last_seen_ms ?? nowMs) : nowMs;
  const elapsedMs = Math.max(0, asAt - live.started_at_ms);
  const totalMs = fixed ? Math.max(1, live.expected_end_ms - live.started_at_ms) : null;
  const leftMs = fixed ? live.expected_end_ms - asAt : null;
  const overrun = fixed && !stale && leftMs <= 0;
  const minsLeft = fixed ? leftMs / 60_000 : null;
  const u = stale ? LIVE_PAUSED : !fixed ? LIVE_OPEN : overrun ? LIVE_OVERRUN : urgency(minsLeft);
  const progress = fixed ? Math.min(100, Math.max(0, (elapsedMs / totalMs) * 100)) : null;
  return { stale, fixed, overrun, asAt, elapsedMs, leftMs, minsLeft, u, progress };
}

// One line for a table cell: "ends 11:00 AM · 40 min left · exp. ₱250".
export function liveLine(live, nowMs = Date.now()) {
  const s = liveState(live, nowMs);
  if (!s) return '';
  const exp = `exp. ${fmtCentavos(live.expected_charge_centavos)}`;
  if (s.stale) return `paused · phone last seen ${fmtAgo(live.last_seen_ms, nowMs)} · ${exp}`;
  if (!s.fixed) return `open time · ${fmtHrMin(s.elapsedMs / 60_000)} so far · ${exp}`;
  if (s.overrun) return `ends ${hhmm(live.expected_end_ms)} · past its end · ${exp}`;
  return `ends ${hhmm(live.expected_end_ms)} · ${fmtHrMin(Math.ceil(s.minsLeft))} left · ${exp}`;
}

/* ── Clock ──────────────────────────────────────────────────────── */

// One-second tick while the card is live; nothing while it is paused.
function useNowTick(active) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
}

/* ── Building blocks ────────────────────────────────────────────── */

const eyebrow = { fontSize: 9, textTransform: 'uppercase', letterSpacing: 2, color: '#555', fontWeight: 700 };

function Figure({ label, value, color = '#fff', testId, sub }) {
  return (
    <div style={{ minWidth: 0 }}>
      <p style={eyebrow}>{label}</p>
      <p style={{ fontSize: 16, fontWeight: 700, color, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} data-testid={testId}>
        {value}
        {sub && <span style={{ fontSize: 11, color: GREY, fontWeight: 400 }}> {sub}</span>}
      </p>
    </div>
  );
}

/* ── The card ───────────────────────────────────────────────────── */

export function LiveCard({ live, audience = 'owner', deviceName, siteName, compact = false, testId = 'kiosk-live' }) {
  const stale = !!live?.stale;
  const now = useNowTick(!!live && !stale);
  useEffect(() => { ensureLiveKeyframes(); }, []);
  const s = liveState(live, now);
  if (!s) return null;

  const court = audience === 'court';
  const { u, fixed, overrun } = s;
  const pulse = u.key === 'red' && !stale;
  const bookedLabel = fixed ? `Fixed · ${fmtHrMin(live.booked_minutes)}` : 'Open time';
  const where = [deviceName, siteName].filter(Boolean).join(' · ');

  // The big number: time left (fixed), time over (overrun), played so far (open).
  let bigLabel, bigValue;
  if (stale) {
    bigLabel = fixed ? 'Left at last contact' : 'Played at last contact';
    bigValue = fixed ? fmtCountdown(Math.max(0, s.leftMs) / 60_000) : fmtCountdown(s.elapsedMs / 60_000);
  } else if (!fixed) {
    bigLabel = 'Playing for';
    bigValue = fmtCountdown(s.elapsedMs / 60_000);
  } else if (overrun) {
    bigLabel = 'Over by';
    bigValue = `+${fmtCountdown(-s.leftMs / 60_000)}`;
  } else {
    bigLabel = 'Time left';
    bigValue = fmtCountdown(s.minsLeft);
  }

  return (
    <div
      data-testid={testId}
      data-stale={stale ? '1' : '0'}
      data-state={u.key}
      style={{
        position: 'relative',
        borderRadius: 14,
        background: stale ? '#0F0F0F' : `linear-gradient(180deg, ${u.bg}, #0D0D0D 60%)`,
        border: `1px solid ${u.border}`,
        boxShadow: stale ? 'none' : `0 0 24px -8px ${u.glow}`,
        overflow: 'hidden',
        color: '#fff',
      }}>

      {/* Header: what, where, and the state pill */}
      <div style={{ padding: compact ? '10px 14px 8px' : '14px 16px 10px', borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
          {stale
            ? <WifiOff className="h-4 w-4" style={{ color: GREY, flexShrink: 0 }} />
            : <span className="sports-live-animated" style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 999, background: AMBER, boxShadow: `0 0 10px ${AMBER}`, flexShrink: 0, animation: 'sports-live-pulse 1.6s ease-in-out infinite' }} />}
          <div style={{ minWidth: 0 }}>
            <p style={eyebrow}>{where || 'Ball machine'}</p>
            <h3 style={{ fontFamily: 'Oswald, sans-serif', fontSize: compact ? 16 : 20, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', lineHeight: 1.1, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: stale ? GREY : '#fff' }}>
              Machine in use
            </h3>
          </div>
        </div>
        <span data-testid={`${testId}-status`} style={{
          fontSize: 9, letterSpacing: 1.5, fontWeight: 800, textTransform: 'uppercase',
          padding: '4px 8px', borderRadius: 999, whiteSpace: 'nowrap', flexShrink: 0,
          color: u.fg, background: u.bg, border: `1px solid ${u.border}`,
        }}>
          <span className="sports-live-animated" style={{ display: 'inline-block', width: 6, height: 6, borderRadius: 999, background: u.fg, marginRight: 6, verticalAlign: 'middle', animation: pulse ? 'sports-live-pulse 1s ease-in-out infinite' : 'none' }} />
          {u.label}
        </span>
      </div>

      <div style={{ padding: compact ? '12px 14px' : 16 }}>
        {/* Now: started / finish on the left, the big number on the right */}
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, marginBottom: fixed ? 10 : 6 }}>
          <div style={{ minWidth: 0 }}>
            <p style={eyebrow}>{bookedLabel}</p>
            <p style={{ color: '#fff', fontWeight: 700, fontSize: 15, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              <span data-testid={`${testId}-started`}>{hhmm(live.started_at_ms)}</span>
              {fixed && <> – <span data-testid={`${testId}-end`} style={{ color: overrun && !stale ? u.fg : '#fff' }}>{hhmm(live.expected_end_ms)}</span></>}
            </p>
            <p style={{ fontSize: 12, color: '#A3A3A3', marginTop: 2 }}>
              {fixed ? 'Started – expected finish' : 'Started · bills the first hour, then by the minute'}
            </p>
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <p style={eyebrow}>{bigLabel}</p>
            <div data-testid={`${testId}-countdown`} style={{
              fontFamily: 'Oswald, sans-serif', fontSize: compact ? 24 : 32, fontWeight: 800,
              color: u.fg, lineHeight: 1, letterSpacing: 1, marginTop: 2,
              textShadow: stale ? 'none' : `0 0 18px ${u.glow}`, fontVariantNumeric: 'tabular-nums',
            }}>
              {bigValue}
            </div>
          </div>
        </div>

        {/* Progress bar: fixed bookings only. Frozen when paused, full when over. */}
        {fixed && (
          <div data-testid={`${testId}-progress`} aria-hidden style={{ height: 4, borderRadius: 999, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
            <div style={{ width: `${overrun ? 100 : s.progress}%`, height: '100%', background: u.fg, boxShadow: stale ? 'none' : `0 0 10px ${u.glow}`, transition: 'width 0.5s linear' }} />
          </div>
        )}

        {stale && (
          <p style={{ fontSize: 12, color: GREY, marginTop: 10 }} data-testid={`${testId}-stale`}>
            The phone stopped reporting {fmtAgo(live.last_seen_ms, now)}. These figures are frozen at its last
            contact, not counting up. The rental may have ended already — the phone&apos;s record will say.
          </p>
        )}

        {/* 2026-09-30: who is playing (asked before the rental) and whether it's a free trial. */}
        {(live.customer_name || live.trial) && (
          <p style={{ fontSize: 13, color: '#fff', fontWeight: 700, marginTop: 10 }} data-testid={`${testId}-name`}>
            {live.customer_name ? `Playing: ${live.customer_name}` : ''}{live.customer_name && live.trial ? ' · ' : ''}{live.trial ? <span style={{ color: GREEN }}>Free trial · ₱0</span> : null}
          </p>
        )}
        {/* Money: expected, and who gets what */}
        <div className="grid grid-cols-2 gap-3" style={{ marginTop: 14 }}>
          <Figure
            label={court ? 'Expected takings' : 'Expected revenue'}
            value={fmtCentavos(live.expected_charge_centavos)}
            color={stale ? GREY : u.fg}
            testId={`${testId}-charge`}
          />
          <Figure
            label={court ? 'Your share' : 'HighMark · court'}
            value={court ? fmtCentavos(live.expected_court_centavos) : fmtCentavos(live.expected_owner_centavos)}
            sub={court ? null : `· ${fmtCentavos(live.expected_court_centavos)}`}
            color={stale ? GREY : GREEN}
            testId={`${testId}-split`}
          />
        </div>

        {live.voucher && (
          <p style={{ fontSize: 12, color: '#4FC3F7', marginTop: 10 }} data-testid={`${testId}-voucher`}>
            Voucher {live.voucher.code}: −{fmtCentavos(live.voucher.discount_centavos)} locked at start
            {court ? ` (you fund ${fmtCentavos(live.voucher.court_funded_centavos)})` : ` (HighMark ${fmtCentavos(live.voucher.owner_funded_centavos)} · court ${fmtCentavos(live.voucher.court_funded_centavos)})`}
          </p>
        )}

        <p style={{ fontSize: 11, color: '#666', marginTop: 10 }}>
          <Activity className="h-3 w-3 inline mr-1" style={{ verticalAlign: -2 }} />
          Expected, not earned: they may extend or stop early. The machine&apos;s final record decides.
        </p>
      </div>
    </div>
  );
}
